import { Link } from "react-router-dom";
import { ArrowRight, BellRing, ClipboardCheck, Monitor, Smartphone, Trophy } from "lucide-react";

import { BODY, C, DISPLAY, SectionHead, cardBodyStyle, cardLight, eyebrowStyle, sectionBase } from "../../pages/Landing";

export const DEMO_TOURNAMENT_SLUG = "sierra-showcase-cup";

export const PLANS = [
  { key: "whiteboard", name: "Whiteboard", price: "Free", unit: "", body: "Up to 8 teams. Live bracket, scores and a public link." },
  { key: "hub", name: "Tournament Hub", price: "$69", unit: "from / event", body: "Divisions, pools, double elimination, check-in, Big Screen, alerts and results graphics.", featured: true },
  { key: "director", name: "Director", price: "$299", unit: "from / year", body: "For directors running several events: Hub events, saved teams, results archive and more." },
];

export function goRunTournament() {
  sessionStorage.setItem("chalkos.afterLogin", "/app/tournaments");
}

export function RunCta({ testId, dark = true, label = "Run a tournament — free" }) {
  return (
    <Link to="/login?mode=signup" onClick={goRunTournament} data-testid={testId}
          className={dark ? "chalk-btn-athletic chalk-btn-athletic--dark" : "chalk-btn-athletic"}
          style={dark ? undefined : { background: "#fff", color: C.greenDark }}>
      {label} <ArrowRight size={14} aria-hidden="true" />
    </Link>
  );
}

export function PlanCards({ testPrefix = "tplan" }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, marginTop: 40 }}>
      {PLANS.map((p) => (
        <div key={p.key} data-testid={`${testPrefix}-${p.key}`} className="chalk-marketing-card"
             style={{ ...cardLight, background: p.featured ? "#1f3b2d" : "#fff", color: p.featured ? C.chalkWhite : C.textDark,
                      border: p.featured ? `1.5px solid ${C.greenChalk}` : cardLight.border, boxShadow: p.featured ? "0 22px 50px rgba(26,58,42,0.32)" : "none" }}>
          <div style={{ fontFamily: DISPLAY, fontSize: 26, letterSpacing: "0.02em", textTransform: "uppercase", lineHeight: 1 }}>{p.name}</div>
          <div style={{ marginTop: 10, display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontFamily: DISPLAY, fontSize: 44, lineHeight: 1 }}>{p.price}</span>
            {p.unit && <span style={{ fontFamily: BODY, fontSize: 13, opacity: 0.7 }}>{p.unit}</span>}
          </div>
          <p style={{ ...cardBodyStyle, color: p.featured ? "rgba(255,255,255,0.75)" : C.muted }}>{p.body}</p>
        </div>
      ))}
    </div>
  );
}

/** Landing-page section. */
export function TournamentSection() {
  return (
    <section data-testid="landing-tournaments" className="chalkboard-texture" style={{ ...sectionBase, background: C.greenDark }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ ...eyebrowStyle, textAlign: "center" }}>NEW · TOURNAMENT MODE</div>
        <div style={{ marginTop: 10 }}>
          <SectionHead title="Run your tournament from your phone." subtitle="Brackets, check-in, alerts. Live." dark />
        </div>
        <p style={{ fontFamily: BODY, fontSize: 17, color: C.chalkMuted, textAlign: "center", maxWidth: 620, margin: "24px auto 0", lineHeight: 1.55 }}>
          Players and fans follow along with a link. No app to download. Scores, locations and announcements update for everyone the moment you tap.
        </p>
        <PlanCards testPrefix="landing-tplan" />
        <div style={{ marginTop: 32, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <RunCta testId="landing-tournament-cta" dark={false} />
          <Link to="/tournaments" data-testid="landing-tournament-more"
                style={{ fontFamily: BODY, fontWeight: 700, fontSize: 13, letterSpacing: "0.1em", textTransform: "uppercase", color: C.greenChalk, padding: "14px 10px", textDecoration: "none" }}>
            How it works & pricing →</Link>
        </div>
      </div>
    </section>
  );
}

export const FEATURES = [
  { Icon: Trophy, name: "Live brackets", body: "Single and double elimination, pools, consolation and Silver brackets. Scores update everyone instantly." },
  { Icon: ClipboardCheck, name: "Check-in", body: "Captains check in from a link. You see who's here, who's late, and who's a no-show." },
  { Icon: BellRing, name: "Announcements", body: "Push alerts to everyone following: delays, location changes, the next game." },
  { Icon: Monitor, name: "Big Screen", body: "Put brackets and the by-location schedule on any TV. Pair it with a 4-digit code." },
  { Icon: Smartphone, name: "No app for players or fans", body: "One public link or QR code. Works in any phone browser." },
];

export const STEPS = [
  { n: "01", title: "Create", body: "Name it, pick your sport, add teams and locations. Two minutes on your phone." },
  { n: "02", title: "Share the link or QR", body: "Text the link, print the QR poster for the check-in table. Fans follow with one tap." },
  { n: "03", title: "Run it live", body: "Enter scores, move games between locations, send alerts. Results graphics are ready when it ends." },
];

export const PRICE_ROWS = [
  ["Whiteboard", "Free", "Up to 8 teams"],
  ["Tournament Hub", "$69 / event", "9–16 teams"],
  ["Tournament Hub", "$99 / event", "17–32 teams"],
  ["Tournament Hub", "$179 / event", "33–64 teams"],
  ["Tournament Hub", "Contact us", "65+ teams"],
  ["Director", "$299 / year", "Up to 6 Hub events a year, up to 32 teams each"],
  ["Director Plus", "$499 / year", "Unlimited Hub events, up to 64 teams each"],
  ["Extra helpers", "$5 / event or $25 / year", "Per helper: Hub events include 3, Director plans include 5"],
];

export const FAQ = [
  ["Do players need the app?", "No. Players, parents and fans follow your tournament from a link or QR code in any phone browser. Only you and your helpers sign in."],
  ["Do I pay more if more teams show up?", "Only the difference, after the event. You pay for today's team count when you upgrade. If more teams check in or play, we email you and charge just the difference 48 hours after the event. If fewer show up, we refund the difference."],
  ["Is youth info private?", "Yes. Youth tournaments show team names only on every public page, the Big Screen, posters and results graphics. Never player names."],
  ["What does \"Location\" mean?", "Any court, field, diamond or rink you're playing on. Name them however your venue does."],
];

